import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { sendPasswordResetEmail } from 'firebase/auth';
import {
  roleSchema,
  unitsResponseSchema,
  userDetailResponseSchema,
  type CreateUserRequest,
  type Role,
  type UserDetail,
} from '@unit/shared';
import { auth, usingEmulators } from '../../lib/firebase.js';
import { apiPost } from '../../lib/api.js';
import { useApi } from '../../lib/useApi.js';
import { useAction } from '../../lib/useAction.js';
import { ActionError } from '../../components/ActionError.js';
import { diffToPatch, flatten } from './PersonalDetailsForm.js';
import { unitOptions } from '../units/unitOptions.js';
import { TitlePicker } from './TitlePicker.js';

const input =
  'w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none';

type Invite = { kind: 'sent' } | { kind: 'failed' } | { kind: 'password' } | { kind: 'none' };

const EMPTY_DETAILS = flatten({
  phone: null,
  personalDetails: {
    dateOfBirth: null,
    address: null,
    bloodType: null,
    sizes: { shirt: null, pants: null, boots: null, vest: null },
    emergencyContact: { name: null, phone: null, relation: null },
  },
});

/**
 * Create one account with everything the file holds (REQUIREMENTS §2.7;
 * owner 2026-09-13: "not just from file").
 *
 * Two ways in. Without a password the server creates the account and this
 * form asks Firebase to email a set-your-password link — the invite. With a
 * password typed here, the admin hands it over themselves and no invite is
 * sent; the server never stores or returns it. If the invite fails to send,
 * the account still exists and the message says so, so the admin sets a
 * password from the soldier's page rather than creating a duplicate.
 */
export function CreateSoldierForm({
  unitId,
  onCreated,
}: {
  /** Preselects the unit — a team, or a platoon itself (story 32); the picker still lets the admin change it. */
  unitId?: string;
  onCreated: (user: UserDetail) => void;
}) {
  const { t } = useTranslation();
  const units = useApi('/units', unitsResponseSchema);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: '',
    personalNumber: '',
    firstName: '',
    lastName: '',
    rank: '',
    title: '',
    unitId: unitId ?? '',
    password: '',
  });
  const [roles, setRoles] = useState<Role[]>(['soldier']);
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [more, setMore] = useState(false);
  const [created, setCreated] = useState<UserDetail | null>(null);
  const [invite, setInvite] = useState<Invite | null>(null);
  // Owner 2026-09-14: the set-your-password email is opt-in, as on the import.
  // Nobody needs it — every account signs in by email link.
  const [sendEmail, setSendEmail] = useState(false);

  const create = useAction((body: CreateUserRequest) => apiPost('/users', userDetailResponseSchema, body));

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const setDetail = (key: keyof typeof details) => (e: { target: { value: string } }) =>
    setDetails((d) => ({ ...d, [key]: e.target.value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setInvite(null);
    const patch = diffToPatch(EMPTY_DETAILS, details);
    const result = await create.run({
      email: form.email,
      personalNumber: form.personalNumber,
      firstName: form.firstName,
      lastName: form.lastName,
      rank: form.rank || null,
      title: form.title || null,
      phone: patch?.phone ?? null,
      unitId: form.unitId,
      roles,
      ...(patch?.personalDetails ? { personalDetails: patch.personalDetails } : {}),
      password: form.password || null,
    });
    if (!result) return;

    setCreated(result.user);
    if (form.password) {
      setInvite({ kind: 'password' });
    } else if (!sendEmail) {
      setInvite({ kind: 'none' });
    } else {
      try {
        await sendPasswordResetEmail(auth, form.email);
        setInvite({ kind: 'sent' });
      } catch {
        setInvite({ kind: 'failed' });
      }
    }
    setForm({ email: '', personalNumber: '', firstName: '', lastName: '', rank: '', title: '', unitId: unitId ?? form.unitId, password: '' });
    setRoles(['soldier']);
    setDetails(EMPTY_DETAILS);
    onCreated(result.user);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="add-soldier"
        className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700"
      >
        {t('users.addSoldier')}
      </button>
    );
  }

  const options = units.kind === 'ready' ? unitOptions(units.data.units, (name) => t('users.directOnPlatoon', { platoon: name })) : [];

  return (
    <form
      onSubmit={(e) => void submit(e)}
      className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200"
      data-testid="create-soldier-form"
    >
      <h3 className="font-semibold text-slate-900">{t('users.addSoldier')}</h3>
      <p className="mt-1 text-xs text-slate-500">{t('users.addSoldierHint')}</p>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-600">
          {t('users.firstName')}
          <input className={input} value={form.firstName} onChange={set('firstName')} required data-testid="new-first-name" />
        </label>
        <label className="text-sm text-slate-600">
          {t('users.lastName')}
          <input className={input} value={form.lastName} onChange={set('lastName')} required data-testid="new-last-name" />
        </label>
        <label className="text-sm text-slate-600">
          {t('org.columns.personalNumber')}
          {/* Digits typed into an RTL field: isolated, so the caret and the
              digits agree on which end is the start. */}
          <input className={`${input} latin-inline`} inputMode="numeric" value={form.personalNumber} onChange={set('personalNumber')} required data-testid="new-personal-number" />
        </label>
        <label className="text-sm text-slate-600">
          {t('users.email')}
          <input className={`${input} latin-inline`} type="email" value={form.email} onChange={set('email')} required data-testid="new-email" />
        </label>
        <label className="text-sm text-slate-600">
          {t('users.rank')}
          <input className={input} value={form.rank} onChange={set('rank')} />
        </label>
        <label className="text-sm text-slate-600">
          {t('org.columns.title')}
          <TitlePicker className={input} value={form.title} onChange={(title) => setForm((f) => ({ ...f, title }))} testId="new-title" />
        </label>
        <label className="text-sm text-slate-600">
          {t('users.unit')}
          <select className={input} value={form.unitId} onChange={set('unitId')} required data-testid="new-team">
            <option value="">—</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm text-slate-600">
          {t('users.password')}
          <input
            className={`${input} latin-inline`}
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={form.password}
            onChange={set('password')}
            data-testid="new-password"
          />
          <span className="block text-xs text-slate-500">{t('users.passwordHint')}</span>
        </label>
        <fieldset className="text-sm text-slate-600 sm:col-span-2">
          <legend>{t('org.columns.roles')}</legend>
          <div className="mt-1 flex flex-wrap gap-3">
            {roleSchema.options.map((role) => (
              <label key={role} className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={roles.includes(role)}
                  onChange={(e) =>
                    setRoles((r) => (e.target.checked ? [...r, role] : r.filter((x) => x !== role)))
                  }
                  data-testid={`new-role-${role}`}
                />
                {t(`roles.${role}`)}
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      <button
        type="button"
        onClick={() => setMore((m) => !m)}
        className="mt-3 text-sm text-slate-600 underline-offset-4 hover:underline"
        data-testid="new-more-details"
      >
        {more ? t('users.lessDetails') : t('users.moreDetails')}
      </button>
      {more && (
        <div className="mt-2 grid gap-3 sm:grid-cols-2" data-testid="new-details">
          {(
            [
              ['phone', t('users.phone'), true],
              ['dateOfBirth', t('users.dateOfBirth'), true],
              ['bloodType', t('users.bloodType'), true],
              ['address', t('users.address'), false],
              ['shirt', t('users.sizes.shirt'), true],
              ['pants', t('users.sizes.pants'), true],
              ['boots', t('users.sizes.boots'), true],
              ['vest', t('users.sizes.vest'), true],
              ['emergencyName', t('users.emergencyName'), false],
              ['emergencyRelation', t('users.emergencyRelation'), false],
              ['emergencyPhone', t('users.emergencyPhone'), true],
            ] as const
          ).map(([key, label, latin]) => (
            <label key={key} className="text-sm text-slate-600">
              {label}
              <input
                className={`${input} ${latin ? 'latin-inline' : ''}`}
                type={key === 'dateOfBirth' ? 'date' : 'text'}
                value={details[key]}
                onChange={setDetail(key)}
                data-testid={`new-pd-${key}`}
              />
            </label>
          ))}
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={create.busy || roles.length === 0}
          data-testid="create-soldier-submit"
          className="rounded-lg bg-slate-900 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-slate-700 disabled:opacity-40"
        >
          {create.busy ? t('users.creating') : form.password || !sendEmail ? t('users.create') : t('users.createAndInvite')}
        </button>
        {!form.password && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} data-testid="new-send-email" />
            {t('users.sendInviteEmail')}
          </label>
        )}
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-lg px-3 py-1.5 text-sm text-slate-600 ring-1 ring-slate-300 transition hover:bg-slate-100"
        >
          {t('org.cancel')}
        </button>
      </div>

      <div className="mt-3">
        <ActionError error={create.error} />
      </div>

      {created && invite && (
        <div
          className={`mt-3 rounded-xl p-3 text-sm ring-1 ${
            invite.kind === 'failed'
              ? 'bg-amber-50 text-amber-900 ring-amber-200'
              : 'bg-emerald-50 text-emerald-900 ring-emerald-200'
          }`}
          data-testid={`invite-${invite.kind}`}
        >
          {invite.kind === 'sent' && t('users.inviteSent', { name: `${created.firstName} ${created.lastName}` })}
          {invite.kind === 'failed' && t('users.inviteFailed', { name: `${created.firstName} ${created.lastName}` })}
          {invite.kind === 'password' && t('users.createdWithPassword', { name: `${created.firstName} ${created.lastName}` })}
          {invite.kind === 'none' && t('users.createdNoEmail', { name: `${created.firstName} ${created.lastName}` })}
          {invite.kind === 'sent' && usingEmulators && (
            <p className="mt-1 text-xs opacity-80">{t('users.inviteEmulatorHint')}</p>
          )}
        </div>
      )}
    </form>
  );
}
